import * as React from "react";
import type { LucideIcon } from "lucide-react";

import { Kbd } from "@/components/ui/kbd";
import { Logo } from "@/components/ui/logo";
import { cn } from "@/lib/utils";

/**
 * EmptyState — what a view shows when it has nothing to list: an
 * invitation, not an apology (CORE-231). Glyph tile, title-2-weight title,
 * one-sentence description, and always the next action — plus the keyboard
 * way to do it.
 *
 *   <EmptyState
 *     icon={FileText}
 *     title="No pages yet"
 *     description="Create your first page to start the knowledge base."
 *     action={<Button size="sm">Create page</Button>}
 *     shortcut={["C"]}
 *   />
 *
 * Omit `icon` to show the animated mark (draws once). `compact` is the
 * inset variant for rails and panels. Never render a bare "nothing here";
 * never use a full-height panel for something that is merely absent (that
 * is DetailRow's job).
 */
export interface EmptyStateProps {
  /** Lucide icon rendered in the glyph tile; omit for the animated mark. */
  icon?: LucideIcon;
  title: React.ReactNode;
  description?: React.ReactNode;
  /** Primary next action (usually a single Button). */
  action?: React.ReactNode;
  /** Keys shown as "or press …" next to the action. */
  shortcut?: string[];
  /** Inset variant for rails and panels. */
  compact?: boolean;
  className?: string;
}

export function EmptyState({
  icon: Icon,
  title,
  description,
  action,
  shortcut,
  compact = false,
  className,
}: EmptyStateProps) {
  return (
    <div
      className={cn(
        "flex flex-col items-center text-center",
        compact ? "gap-2 p-6" : "gap-2 px-6 py-12",
        className,
      )}
    >
      <div
        className={cn(
          "mb-2 inline-flex size-14 items-center justify-center rounded-lg border border-line-100 bg-bg-200 text-ink-200 shadow-card",
          compact && "size-10",
        )}
      >
        {Icon ? (
          <Icon aria-hidden="true" className={compact ? "size-4" : "size-5"} />
        ) : (
          <Logo variant="mark" size={compact ? 20 : 28} animate="once" />
        )}
      </div>
      <h2 className="text-base font-semibold leading-snug tracking-tight text-ink-100">
        {title}
      </h2>
      {description ? (
        <p className="max-w-[360px] text-[13px] leading-snug text-ink-200">
          {description}
        </p>
      ) : null}
      {action || shortcut ? (
        <div className="mt-3 flex items-center gap-3">
          {action}
          {shortcut ? (
            <span className="inline-flex items-center gap-1.5 text-xs text-ink-300">
              or press
              <span className="inline-flex" aria-hidden="true">
                {shortcut.map((key) => (
                  <Kbd key={key}>{key}</Kbd>
                ))}
              </span>
              <span className="sr-only">{shortcut.join(" then ")}</span>
            </span>
          ) : null}
        </div>
      ) : null}
    </div>
  );
}
